import { createImageVariantConfig } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { constructQueryParamName, isOriginInUse, isStockInUse } from '../../util/search';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { parse } from '../../util/urlHelpers';
import { parseDateContext, datesQueryParams, DATE_MODE_QUERY_PARAM } from '../../util/searchDates';
import { FEED_IMAGE_LIMIT } from '../../util/listingImages';
import { HOUR, isBookingProcessAlias } from '../../transactions/transaction';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const FETCH_PAGE_AVAILABILITY_REQUEST = 'app/SearchPage/FETCH_PAGE_AVAILABILITY_REQUEST';
export const FETCH_PAGE_AVAILABILITY_SUCCESS = 'app/SearchPage/FETCH_PAGE_AVAILABILITY_SUCCESS';
export const FETCH_PAGE_AVAILABILITY_ERROR = 'app/SearchPage/FETCH_PAGE_AVAILABILITY_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  // Which of the listings on the current page are free on the searched date, as id strings.
  //
  // `null` is a real value here and means "not resolved": no date is being searched, the probe
  // is still in flight, or it failed. An empty array is the opposite claim — "resolved, and
  // nobody is free" — so the two must never be conflated. A failed probe that fell back to []
  // would mark every supplier on the page as booked.
  availableListingIds: null,
  // Identifies the probe whose answer this slice is willing to accept. Availability probes are
  // fired per page and are not cancellable, so paging 1 -> 2 quickly can land page 1's slower
  // answer after page 2's. Without this, page 1's availability would be painted onto page 2's
  // cards — a supplier who is free shown as booked. Only the response whose token matches the
  // request in flight is allowed to write.
  pageAvailabilityToken: null,
  // The searchParams that produced currentPageResultIds. searchParams itself cannot say this: it is
  // overwritten the moment a search starts, while the previous results stay on screen until the
  // response lands. Compared with the URL, this is what tells the same search re-fetching (Back,
  // or the client re-running the server's search after hydration) from a different search on its
  // way.
  currentPageResultsSearchParams: null,
};

const publishedListings = data =>
  data.data.filter(l => !l.attributes.deleted && l.attributes.state === 'published');

const resultIds = data => publishedListings(data).map(l => l.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
        // The answer belonged to the previous page and the previous date. Holding on to it
        // would hide one page's booked suppliers out of another page's results. Clearing the
        // token with it also disowns any probe still in flight from that previous search.
        availableListingIds: null,
        pageAvailabilityToken: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        currentPageResultsSearchParams: payload.searchParams || null,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case FETCH_PAGE_AVAILABILITY_REQUEST:
      return { ...state, availableListingIds: null, pageAvailabilityToken: payload.token };
    case FETCH_PAGE_AVAILABILITY_SUCCESS:
      // A response that does not answer the request in flight is stale: a probe from a page or a
      // date the user has already left. Dropping it is the whole point of the token.
      return payload.token !== state.pageAvailabilityToken
        ? state
        : { ...state, availableListingIds: payload.listingIds };
    case FETCH_PAGE_AVAILABILITY_ERROR:
      // Deliberately leaves availableListingIds at null. Availability is unknown, not empty:
      // showing the page unfiltered is the right way to be wrong, emptying it is not.
      // Guarded by the same token, so a stale failure cannot disown a live probe.
      return payload.token !== state.pageAvailabilityToken
        ? state
        : { ...state, availableListingIds: null };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = (response, searchParams) => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data, searchParams },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchPageAvailabilityRequest = token => ({
  type: FETCH_PAGE_AVAILABILITY_REQUEST,
  payload: { token },
});

export const fetchPageAvailabilitySuccess = (listingIds, token) => ({
  type: FETCH_PAGE_AVAILABILITY_SUCCESS,
  payload: { listingIds, token },
});

export const fetchPageAvailabilityError = (e, token) => ({
  type: FETCH_PAGE_AVAILABILITY_ERROR,
  error: true,
  payload: { error: e, token },
});

/**
 * Stock filters for the main search query.
 *
 * This deliberately does NOT carry availability params. The Marketplace API cannot paginate an
 * availability-filtered query — it answers `{"totalItems":null,"totalPages":null,
 * "paginationUnsupported":true}` and rejects `page=2` with a 400 — so a dated search over this
 * 377-listing catalogue would be capped at one page of 24 with no result count at all. The main
 * query therefore stays a plain, paginated superset, and availability is resolved afterwards,
 * one page at a time, by availabilityProbeParams below.
 *
 * If you find yourself adding `start`, `end`, `availability` or `minDuration` back in here, the
 * pagination goes with them. SearchPage.duck.test.js asserts their absence.
 *
 * Stock filtering is independent of date filtering: minStock excludes explicitly sold-out
 * listings, and stockMode 'match-undefined' keeps bookable and inquiry listings that carry no
 * stock at all. Previously the stock filters were dropped whenever dates were in use, which
 * made sold-out products reappear the moment a buyer picked a date; they are unconditional here.
 *
 * It takes no arguments any more: there is no date input left for it to read. The name and the
 * call site are kept so that the one place the stock filters are assembled stays the one place
 * a reader looks for them.
 *
 * @returns {Object} params to spread into sdk.listings.query
 */
export const buildDateAndStockParams = () => ({ minStock: 1, stockMode: 'match-undefined' });

/**
 * Params for the follow-up query that asks "which of THESE listings are free on that date?".
 *
 * Bounded to the ids of the page being viewed, so it stays a single small request no matter how
 * large the result set is, and so the answer is exactly as paginated as the page it describes.
 * The spike against the live marketplace confirmed that `ids` + availability params is accepted
 * and returns precisely the subset of the requested ids that match.
 *
 * The window itself is built by src/util/searchDates.js, which queries Europe/Zurich directly
 * rather than padding a UTC window to cover every time zone — see that module's comments. Using
 * the same builder as before means the question being asked has not changed; only where it is
 * asked has.
 *
 * Returns null whenever there is nothing to ask — no date, no ids, or a date that has already
 * passed — so the caller can skip the request rather than fire an empty one.
 *
 * @param {Array<string>} listingIds ids of the listings on the current page
 * @param {string|null|undefined} datesParam raw `dates` query param
 * @param {string|null|undefined} dateModeParam raw `dateMode` query param, 'month' or absent
 * @param {Date} [now] injectable for tests; defaults to the current time
 * @param {string} [unitType] unit type shared by these listings; 'hour' asks a looser question
 * @returns {Object|null} params to spread into sdk.listings.query, or null for "do not ask"
 */
export const availabilityProbeParams = (
  listingIds,
  datesParam,
  dateModeParam,
  now = new Date(),
  unitType = null
) => {
  const dateParams = datesQueryParams(parseDateContext(datesParam, dateModeParam), now, unitType);
  const hasDate = Object.keys(dateParams).length > 0;
  return hasDate && listingIds?.length > 0 ? { ids: listingIds, ...dateParams } : null;
};

/**
 * Identifies one probe by the exact question it asks.
 *
 * The ids alone are not enough: changing only the date re-probes the same page, so the same ids
 * with a different date must count as a different question. The dates and the mode go in with
 * them.
 */
const probeToken = (listingIds, datesParam, dateModeParam) =>
  `${datesParam}|${dateModeParam}|${listingIds.join(',')}`;

/**
 * Resolve which of one page's listings are actually free on the searched date.
 *
 * Failure is deliberately soft. The probe narrows results that are already on screen, so a
 * network blip must leave availability unresolved (null) rather than resolved-and-empty — a
 * search page that hides nobody is a far better outcome than one that hides everybody and tells
 * every couple that every supplier is booked. It never rejects, so callers do not have to
 * handle it.
 *
 * Responses are matched to the request in flight by token, so a slow answer about a page the
 * user has already left is dropped rather than painted over the page they are looking at.
 *
 * @param {Array<string>} listingIds ids of the bookable listings on the current page
 * @param {string|null|undefined} datesParam raw `dates` query param
 * @param {string|null|undefined} dateModeParam raw `dateMode` query param
 * @param {Array<string>} [hourlyIds] those of listingIds that are booked by the hour
 */
export const fetchPageAvailability = (listingIds, datesParam, dateModeParam, hourlyIds = []) => (
  dispatch,
  getState,
  sdk
) => {
  const now = new Date();
  const isHourly = id => hourlyIds.includes(id);
  const probes = [
    availabilityProbeParams(
      listingIds.filter(id => !isHourly(id)),
      datesParam,
      dateModeParam,
      now
    ),
    availabilityProbeParams(listingIds.filter(isHourly), datesParam, dateModeParam, now, HOUR),
  ].filter(Boolean);

  // Nothing to ask. The state is already null from SEARCH_LISTINGS_REQUEST, which is exactly
  // what "no date is being searched" should look like.
  if (probes.length === 0) {
    return Promise.resolve(null);
  }

  const token = probeToken(listingIds, datesParam, dateModeParam);
  dispatch(fetchPageAvailabilityRequest(token));

  return Promise.all(
    probes.map(probeParams =>
      sdk.listings.query({
        ...probeParams,
        // One page of ids in, at most one page of ids out. This query is availability-filtered
        // and so cannot be paginated either — asking for the whole page in one go is what keeps
        // that from mattering.
        perPage: probeParams.ids.length,
        // Only the ids are read from the response; `state` is the cheapest field to ask for.
        'fields.listing': ['state'],
      })
    )
  )
    .then(responses => {
      const ids = responses.flatMap(response => response.data.data.map(l => l.id.uuid));
      dispatch(fetchPageAvailabilitySuccess(ids, token));
      return ids;
    })
    .catch(e => {
      dispatch(fetchPageAvailabilityError(storableError(e), token));
      return null;
    });
};

export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = (listingTypes, listingTypePathParam, isListingTypeVariant) => {
    return isListingTypeVariant
      ? {
          pub_listingType: listingTypePathParam,
        }
      : config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const constructCategoryPropertiesForAPI = (queryParamPrefix, categories, level, params) => {
    const levelKey = `${queryParamPrefix}${level}`;
    const levelValue =
      typeof params?.[levelKey] !== 'undefined' ? `${params?.[levelKey]}` : undefined;
    const foundCategory = categories.find(cat => cat.id === levelValue);
    const subcategories = foundCategory?.subcategories || [];
    // Note: we might need to prepare nested categories too: categoryLevel1, categoryLevel2, categoryLevel3
    return foundCategory && subcategories.length > 0
      ? {
          [levelKey]: levelValue,
          ...constructCategoryPropertiesForAPI(queryParamPrefix, subcategories, level + 1, params),
        }
      : foundCategory
      ? { [levelKey]: levelValue }
      : {};
  };

  /**
   * Category filter params are prepared here. We omit invalid category names.
   * I.e. params that are not part of the currently configured category tree.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareCategoryParams = (paramName, params) => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(categoryParamPrefix)
      ? constructCategoryPropertiesForAPI(categoryParamPrefix, categories, 1, params)
      : {};
  };

  const constructIntegerRangePropertyForAPI = (queryParamPrefix, params) => {
    const integerValue = params?.[queryParamPrefix];
    const [min, max] = integerValue ? integerValue.split(',') : [];
    // NOTE: long filter needs exclusive max value on API side
    const inclusiveMin = Number.parseInt(min, 10);
    const exclusiveMax = Number.parseInt(max, 10) + 1;

    // NOTE: currently we don't validate the range values against the integer range config,
    // but we might want to do that in the future.

    return Number.isInteger(inclusiveMin) && Number.isInteger(exclusiveMax)
      ? { [queryParamPrefix]: [inclusiveMin, exclusiveMax].join(',') }
      : {};
  };

  /**
   * Integer range filter values are converted to API params of type 'long'.
   *
   * The range end must be exclusive. E.g. 1000,2000 -> 1000,2001
   *
   * NOTE: currently we don't validate the range values against the integer range config,
   * but we might want to do that in the future.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareIntegerRangeParam = (paramName, params) => {
    const integerRangeConfig = config.listing.listingFields?.find(f => f.schemaType === 'long');
    const { key, scope } = integerRangeConfig || {};
    const integerParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(integerParamPrefix)
      ? constructIntegerRangePropertyForAPI(integerParamPrefix, params)
      : {};
  };

  // This function goes through given params and if there's a specific handler for the parameter type,
  // it calls the handler to prepare the property for API.
  // Otherwise, it just passes the param through.
  const prepareAPIParams = (params, paramHandlers) => {
    const pickedKeys = Object.entries(params).reduce((picked, [k, v]) => {
      const preparedParams = paramHandlers.reduce((picked, fn) => {
        return { ...picked, ...fn(k, params) };
      }, {});

      // If the param is not handled by any of the handlers, we pass it through.
      const currentParam = Object.keys(preparedParams).length > 0 ? preparedParams : { [k]: v };

      return { ...picked, ...currentParam };
    }, {});

    return pickedKeys;
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const {
    perPage,
    price,
    dates,
    // Pulled out of restOfParams for the same reason `dates` is: it is search input, not an
    // API param, and passing it through to sdk.listings.query would be an unknown-param error.
    [DATE_MODE_QUERY_PARAM]: dateMode,
    // `seats` is an availability filter: the API only accepts it alongside start/end/
    // availability, and the main query deliberately carries none of those (see
    // buildDateAndStockParams). It is pulled out here so it is not passed through as an
    // unknown param, and then dropped. It is not one of this marketplace's configured
    // filters; if it is ever enabled, it belongs on the probe query beside the availability
    // params, not on this one.
    seats,
    // Availability params are pulled out and dropped no matter where they came from. Everything
    // left in restOfParams is passed through to the API verbatim, so a hand-typed or bookmarked
    // `?availability=time-full&start=...` would otherwise reach the query and silently take the
    // whole result set back down to a single unpaginated page. This is the same invariant
    // buildDateAndStockParams holds, enforced against the URL rather than against our own code.
    start,
    end,
    availability,
    minDuration,
    sort,
    mapSearch,
    listingTypePathParam,
    isListingTypeVariant,
    ...restOfParams
  } = searchParams;
  // The params related to default filters are prepared one-by-one
  // We could consider moving them to the prepareAPIParams function too.
  const priceMaybe = priceSearchParams(price);
  const dateAndStockMaybe = buildDateAndStockParams();
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : { sort };

  const params = {
    // The params that are related to listing fields and categories are prepared here.
    // We add handler functions that check category and integer range configurations.
    // - With category params, we essentially just omit invalid category names.
    //   I.e. params that are not part of the currently configured category tree.
    // - With integer range params, we prepare the property for API.
    //   I.e. the range end must be exclusive. E.g. 1000,2000 -> 1000,2001
    // Note: invalid independent search params are still passed through
    ...prepareAPIParams(restOfParams, [prepareCategoryParams, prepareIntegerRangeParam]),
    // If the search page variant is of type /s/:listingType, this sets the pub_listingType
    // query parameter to the value of the listing type path parameter. The ordering matters here,
    // since this value overrides any possible pub_listingType value coming from query parameters
    // i.e. the previous row.
    //
    // Only one value is currently supported in pub_listingType – if you want to support e.g.
    // /s/:listingType?pub_listingType=[otherListingType] => pub_listingType=listingType,otherListingType,
    // you'll need to customize a logic that merges the query param and path param values.
    ...searchValidListingTypes(
      config.listing.listingTypes,
      listingTypePathParam,
      isListingTypeVariant
    ),
    ...priceMaybe,
    ...dateAndStockMaybe,
    ...sortMaybe,
    perPage,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(searchListingsSuccess(response, searchParams));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(searchListingsError(error));
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        throw e;
      }
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const { listingType: listingTypePathParam } = params || {};
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { page = 1, address, origin, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const listingTypeVariantMaybe = listingTypePathParam
    ? { listingTypePathParam, isListingTypeVariant: true }
    : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const searchListingsCall = searchListings(
    {
      ...rest,
      ...originMaybe,
      ...listingTypeVariantMaybe,
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        // These help rendering of 'purchase' listings,
        // when transitioning from search page to listing page
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
        // Load-bearing, not decorative. SearchResultsPanel hides a listing only when it HAS an
        // availabilityPlan and is absent from the resolved id set; a listing with no plan keeps no
        // calendar and is never date-constrained. Sparse fieldsets mean an unrequested attribute
        // comes back undefined, so omitting this makes every listing look plan-less, and the
        // date filtering silently degrades to a no-op that hides nobody. The precedent for
        // asking a public listing resource for it is InboxPage.duck.js.
        'availabilityPlan',
        // Listing badges: 'new' is computed from these, 'popular' is written by the metrics job.
        'createdAt',
        'metadata.badges',
        'metadata.publishedAt',
        // The card's stars, written by server/jobs/reviewStats.js.
        'metadata.reviewStats',
      ],
      // profile.publicData carries the provider's awards, shown as the 'Ausgezeichnet' badge.
      'fields.user': ['profile.displayName',
        'profile.abbreviatedName',
        'profile.publicData',
        'profile.metadata',],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-300`,
        `variants.${variantPrefix}-600`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-300`, 300, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-600`, 600, aspectRatio),
      'limit.images': FEED_IMAGE_LIMIT,
    },
    config
  );

  // The probe is chained here, rather than fired from the component, because loadData is already
  // the thing that runs exactly when the question changes. Routes.js calls it on mount and on
  // every location change, and page, dates and dateMode all live in the URL — so a new page or a
  // new date re-runs it, and an unrelated re-render (a filter popup opening, a hover on the map)
  // does not. A useEffect in SearchPageWithGrid would have to re-derive that same trigger from
  // props and would fire a second time on hydration; this way there is one loader, one trigger,
  // and the ids come straight from the response that produced them.
  //
  // It is deliberately NOT awaited. loadData resolves on the main search alone, so the probe
  // never delays the cards: server/dataLoader.js blocks on this promise, and awaiting the probe
  // would put a second serial round-trip in front of every dated search's first byte. It would
  // buy nothing, because Routes.js re-runs loadData on client mount anyway and that dispatches
  // SEARCH_LISTINGS_REQUEST, which resets availableListingIds to null — so the unfiltered moment
  // exists either way. That moment is what the unresolved `null` state is for: the whole page
  // first, the booked suppliers dropped out of it a beat later. fetchPageAvailability never
  // rejects, so nothing here can go unhandled.
  //
  // For the same reason it is skipped entirely on the server. The client's re-run of loadData
  // resets availableListingIds to null before anything the server resolved could be rendered, so
  // an SSR probe is a Marketplace API round trip per dated page load whose answer is guaranteed
  // to be thrown away. `typeof window` is how this codebase asks (see ListingPage.duck.js's
  // fetchMonthlyTimeSlots and util/sdkLoader.js).
  const hasWindow = typeof window !== 'undefined';

  return dispatch(searchListingsCall).then(response => {
    if (!hasWindow) {
      return response;
    }
    const bookable = response?.data
      ? publishedListings(response.data).filter(l =>
          isBookingProcessAlias(l.attributes.publicData?.transactionProcessAlias)
        )
      : [];
    const hourly = bookable.filter(l => l.attributes.publicData?.unitType === HOUR);
    const uuids = listings => listings.map(l => l.id.uuid);
    dispatch(
      fetchPageAvailability(uuids(bookable), rest.dates, rest[DATE_MODE_QUERY_PARAM], uuids(hourly))
    );
    return response;
  });
};
